/**
 * D-EZ-110 — THE CHAPTER MARKER ON THE SEASON HOME.
 *
 * Before race 1: the season's opening page — the preseason preview's headline
 * and, a press away, the whole edition (and Hall of Fame night when the winter
 * produced a class). After the opener, at halfway, at the cut and when the
 * final four is set: that chapter of the season, read off the races run so far
 * and nothing after (`chapterAt`). Every other week: nothing — a chapter that
 * turned up every week would not be a chapter.
 */

import { useMemo, useState } from 'react';
import type { SeasonState } from '../../domain/seasonPlay.ts';
import type { CareerSeason } from '../../lib/careerRunner.ts';
import {
  CHAPTER_RACES,
  chapterAt,
  hallOfFameNight,
  liveChapterSeason,
  preseasonPreview,
  type ChapterSeason,
} from '../../domain/chapters/index.ts';
import { HallOfFameNightView, PreseasonPreviewView } from './ChapterViews.tsx';

export interface SeasonChapterCardProps {
  state: SeasonState;
  seasonNumber: number;
  teams: readonly { teamId: string; name: string; driverIds: readonly string[] }[];
  history?: readonly CareerSeason[];
  career?: CareerSeason | null;
}

const MARKS = new Set<number>(Object.values(CHAPTER_RACES));

export default function SeasonChapterCard({ state, seasonNumber, teams, history = [], career = null }: SeasonChapterCardProps) {
  const [open, setOpen] = useState<'preview' | 'hof' | null>(null);
  const justRun = state.nextRace - 1;
  const seasons = useMemo<ChapterSeason[]>(() => {
    const before = [...history, ...(career ? [career] : [])].filter((s) => s.seasonNumber < seasonNumber) as unknown as ChapterSeason[];
    return [...before, liveChapterSeason({ seasonNumber, roster: state.roster, teams, state })];
  }, [history, career, seasonNumber, state, teams]);

  const opening = state.races.length === 0;
  const preview = useMemo(() => (opening ? safe(() => preseasonPreview(seasons, seasonNumber)) : null), [opening, seasons, seasonNumber]);
  const night = useMemo(() => (opening ? safe(() => hallOfFameNight(seasons, seasonNumber)) : null), [opening, seasons, seasonNumber]);
  const chapter = useMemo(
    () => (!opening && MARKS.has(justRun) && justRun < CHAPTER_RACES.finale ? safe(() => chapterAt(seasons, seasonNumber, justRun)) : null),
    [opening, justRun, seasons, seasonNumber],
  );

  if (opening && preview) {
    return (
      <section className="chapcard chapcard--open readable" aria-label={`Season ${seasonNumber} begins`}>
        <p className="chap__kicker">Season {seasonNumber} begins · {preview.masthead}</p>
        {open !== 'preview' && <h3 className="chapcard__title">{preview.headline.text}</h3>}
        {open !== 'preview' && <p className="chapcard__line">{preview.dek.text}</p>}
        <div className="chapcard__acts">
          <button type="button" className="btn" aria-expanded={open === 'preview'} onClick={() => setOpen(open === 'preview' ? null : 'preview')}>
            {open === 'preview' ? 'Close the preview' : 'Read the preseason preview'}
          </button>
          {night && (
            <button type="button" className="btn btn--quiet" aria-expanded={open === 'hof'} onClick={() => setOpen(open === 'hof' ? null : 'hof')}>
              {open === 'hof' ? 'Close Hall of Fame night' : 'Hall of Fame night'}
            </button>
          )}
        </div>
        {open === 'preview' && <PreseasonPreviewView preview={preview} />}
        {open === 'hof' && night && <HallOfFameNightView night={night} />}
      </section>
    );
  }
  if (chapter && chapter.lines.length > 0) {
    return (
      <section className="chapcard readable" aria-label={`Season ${seasonNumber}: ${chapter.title}`}>
        <p className="chap__kicker">Season {seasonNumber} · after race {chapter.raceNumber}</p>
        <h3 className="chapcard__title">{chapter.title}</h3>
        {chapter.lines.map((l) => (
          <p key={l.text} className="chapcard__line">{l.text}</p>
        ))}
      </section>
    );
  }
  return null;
}

function safe<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}
